import React from 'react';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { Platform, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useModal } from 'react-native-modalfy';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import {
  BaseText,
  CoverView,
  Icon,
  Image,
  Touchable,
  Separator,
} from '@components';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import { createAspectRatioStyles } from '@styles';
import { modals } from '@modals';
import {
  HubNavigatorRoutePropType,
  MainStackParamListType,
  screens,
} from '@navigation';

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  flex: 1,
});

const CoverViewContainer = styled(CoverView)({
  width: '51%',
});

const ItemImage = styled(Image)({
  ...createAspectRatioStyles(16 / 9, '100%'),
  borderRadius: 12,
  bg: '$background20',
});

const TextContainer = styled(View)({
  flex: 1,
  ml: '$5',
});

interface ITitleStyleProps {
  isHeading: boolean;
}

const Title = styled(BaseText)(({ isHeading }: ITitleStyleProps) => ({
  color: isHeading ? '$primary300' : '$heading800',
  fontWeight: 'semi',
  variant: 'text.medium',
}));

const coverViewStyles: SxProp = {
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 12,
  overflow: 'hidden',
};

const ItemImageCoverBackground = styled(View)({
  zIndex: -1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  bg: '$heading800',
  opacity: 0.7,
});

const WatchedItemImageCoverBackground = styled(View)({
  zIndex: -1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  bg: '$primary300',
  opacity: 0.8,
});

const TimeContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const TimeText = styled(BaseText)({
  color: '$text100',
  variant: 'text.xsmall',
  ml: '$2',
});

export interface IPlaylistItemProps {
  item: Tutorial;
  onPress(item: Tutorial): void;
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

type NavigationRoutePropType = HubNavigatorRoutePropType<'Tutorial'>;

export const PlaylistItem = observer<IPlaylistItemProps>(
  ({ item, onPress }) => {
    const { params } = useRoute<NavigationRoutePropType>();
    const modal = useModal();
    const nav = useNavigation<NavigationPropType>();

    function navigateToSubscription() {
      nav.navigate(screens.ProfileNavigator, {
        screen: screens.Subscription,
      });
    }

    function onPressItem() {
      if (item.isAvailableForCurrentPlan) {
        onPress(item);
      } else {
        modal.openModal(modals.SubscribeSuggestionModal, {
          onPress: () => {
            navigateToSubscription();
            modal.closeModal();
          },
        });
      }
    }

    const isWatchedCourseItem = !!(
      params.courseId && item.userStats?.isWatched
    );
    const isAvailableForCurrentPlan = item.isAvailableForCurrentPlan;
    const isCurrentItem = params.id === item.id;

    const CoverComponent = () => {
      if (!isAvailableForCurrentPlan) {
        return (
          <React.Fragment>
            <ItemImageCoverBackground />
            <Icon
              name="lock-light"
              color="$background0"
              size={Platform.select({ default: 20, web: 30 })}
            />
          </React.Fragment>
        );
      }

      if (isWatchedCourseItem) {
        return (
          <React.Fragment>
            <WatchedItemImageCoverBackground />
            <Icon
              name="done-circule-bold"
              color="$primary80"
              size={55}
            />
          </React.Fragment>
        );
      }

      return null;
    };

    return (
      <TouchableContainer onPress={onPressItem}>
        <CoverViewContainer
          isVisible={
            !isAvailableForCurrentPlan || isWatchedCourseItem
          }
          coverComponentSx={coverViewStyles}
          CoverComponent={CoverComponent}
        >
          <ItemImage
            source={{
              uri: item.poster.normal?.url,
            }}
          />
        </CoverViewContainer>
        <TextContainer>
          <Title isHeading={isWatchedCourseItem || isCurrentItem}>
            {item.title}
          </Title>
          <Separator size={['$2', '$4']} />
          <TimeContainer>
            <Icon
              color="$text100"
              size={12}
              name="time-circle-light"
            />
            <TimeText>{item.formattedDuration}</TimeText>
          </TimeContainer>
        </TextContainer>
      </TouchableContainer>
    );
  },
);
